import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@/shared/api';
import { type Voice, type VoiceShort } from '@intone/shared/types';

/** Fetch all voices for selector */
export function useVoicesList() {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: ['voices-list'],
    queryFn: async () => apiClient<VoiceShort[]>('/voices/list'),
  });
}

/** Fetch a voice by id */
export function useVoice(id: number | null, isNewVoice: boolean ) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ['voices', id],
    queryFn: async () => apiClient<Voice>(`/voices/${id}`),
    enabled: !isNewVoice && !!id,
    initialData: () => {
      return queryClient.getQueryData(['voices', id]);
    },
    staleTime: 1000 * 60,
  });
}

/** Update or create a voice */
export function useUpsertVoice() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (voice: Voice) => {
      return apiClient<Voice>('/voices', {
        method: 'POST',
        body: JSON.stringify(voice),
      });
    },
    onMutate: async (updatedItem) => {
      if (!updatedItem.id) return;

      const listKey = ['voices-list'];
      const voicesKey = ['voices', updatedItem.id];

      await queryClient.cancelQueries({ queryKey: listKey });
      await queryClient.cancelQueries({ queryKey: voicesKey });

      const prevList = queryClient.getQueryData(listKey);
      const prevVoice = queryClient.getQueryData(voicesKey);

      queryClient.setQueryData(listKey, (old: VoiceShort[] = []) =>
        old.map((item) => (item.id === updatedItem.id ? { ...item, ...updatedItem } : item))
      );

      queryClient.setQueryData(voicesKey, (old: Voice) => ({ ...old, ...updatedItem }));

      return { listKey, voicesKey, prevList, prevVoice };
    },
    onError: (_, oldData, context) => {
      if (oldData.id && context) {
        queryClient.setQueryData(context.listKey, context.prevList);
        queryClient.setQueryData(context.voicesKey, context.prevVoice);
      }
    },
    onSuccess: (savedRecord) => {
      queryClient.setQueryData(['voices', savedRecord.id], savedRecord);

      queryClient.setQueryData(['voices-list'], (old: VoiceShort[] = []) => {
        const isExisting = old.some(item => item.id === savedRecord.id);
        return isExisting
          ? old.map(item => item.id === savedRecord.id ? savedRecord : item)
          : [...old, savedRecord];
      });
    },
  });
}

/** Clone a voice */
export function useCloneVoice() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      return apiClient<Voice>(`/voices/${id}/clone`, { method: 'POST' });
    },
    onSuccess: (newVoice) => {
      queryClient.invalidateQueries({ queryKey: ['voices-list'] });
      queryClient.setQueryData(['voices', newVoice.id], newVoice);
    },
  });
}

/** Delete a voice */
export function useDeleteVoice() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      return apiClient<number>(`/voices/${id}`, {
        method: 'DELETE',
      });
    },
    onMutate: async (deletedId) => {
      const queryKey = ['voices-list'];
      await queryClient.cancelQueries({ queryKey: queryKey });
      const previousList = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (old: VoiceShort[] =[]) => (old.filter((item) => item.id !== deletedId)));

      return { queryKey, previousList };
    },
    onError: (_, __, context) => {
      if (context) {
        queryClient.setQueryData(context.queryKey, context.previousList);
      }
    },
    onSuccess: (voiceId) => {
      queryClient.removeQueries({ queryKey: ['voices', voiceId] });
    },
  });
}